import { App } from '../state/app.actions';
import { UserService } from '../services/user/user.service';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IRole } from '../models/role';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  constructor(
    public userService: UserService,
    private router: Router,
    private store: Store
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Idempotent — resolves instantly once session state has already settled
    // out of 'checking' (see ApplicationState.initializeSession), so this is
    // safe to dispatch on every navigation, not just the first.
    return this.store.dispatch(new App.Actions.InitializeSession()).pipe(
      map(() => {
        const authStatus = this.store.snapshot().application.authStatus;

        if (authStatus !== 'authenticated') {
          return this.router.createUrlTree(['/authentication/page-login'], {
            queryParams: { returnUrl: state.url },
          });
        }

        return this.checkPermission(route);
      })
    );
  }

  private checkPermission(route: ActivatedRouteSnapshot): boolean {
    const action = route.data.action || '*';

    // Wildcard routes only require a valid, authenticated session.
    if (action === '*') {
      return true;
    }

    const roles: IRole[] = this.store.snapshot().application.roles || [];
    const userOrgRoles: string[] = this.store.snapshot().user.currentOrganization
      ? this.store.snapshot().user.currentOrganization.roles
      : [];

    const rolesAndActions = roles.filter(
      (currentRole) => !userOrgRoles || userOrgRoles.includes(currentRole.role)
    );
    let actions = [].concat(
      ...rolesAndActions.map(({ actions }) => actions.map((o) => o))
    );
    actions = [...new Set(actions)];

    if (!actions || actions.length === 0) return true; // no actions assigned → allow

    const isSingleAction = typeof action === 'string';
    return isSingleAction
      ? actions.includes(action) // action string
      : actions.some((assignedAction) => action.includes(assignedAction)); // action array
  }
}
